import { useEffect } from 'react';

const SEO = ({ 
  title = "Your Broker Portugal | Consultores Imobiliários de Luxo em Portugal",
  description = "Your Broker Portugal - Consultores imobiliários especializados em propriedades de luxo em Lisboa, Porto, Algarve, Comporta e todo Portugal. Representamos exclusivamente compradores com acesso total ao mercado.",
  keywords = "imóveis Portugal, propriedades luxo Portugal, imobiliária Lisboa, casas luxo Algarve, Comporta imóveis, consultores imobiliários Portugal, buying agent Portugal, real estate Portugal, luxury homes Portugal, Lisboa Prime, Porto imóveis, investimento imobiliário Portugal, Golden Visa Portugal",
  ogImage = "https://customer-assets.emergentagent.com/job_f38ad5a1-d236-45ee-88b7-e3d9b27d3481/artifacts/ode9rwtr_DFF9F010-F752-4035-B3AD-E6BC06DED29B.png",
  url = "https://yourbrokerportugal.com",
  type = "website" // 'website' or 'article'
}) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Your Broker Portugal');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', 'Portuguese');
    updateMetaTag('revisit-after', '7 days');
    
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Your Broker Portugal', true);
    const isPt = window.location.pathname === '/pt' || window.location.pathname.startsWith('/pt/');
    updateMetaTag('og:locale', isPt ? 'pt_PT' : 'en_US', true);
    updateMetaTag('og:locale:alternate', isPt ? 'en_US' : 'pt_PT', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // hreflang alternates — derive EN/PT URLs from the current canonical path.
    // The canonical `url` prop is always a full https URL; we work out the
    // language pair from the current window pathname.
    try {
      const origin = 'https://yourbrokerportugal.com';
      const path = window.location.pathname || '/';
      const isPt = path === '/pt' || path.startsWith('/pt/');
      const bare = isPt ? (path === '/pt' ? '/' : path.slice(3)) : path;
      const enUrl = `${origin}${bare === '/' ? '/' : bare}`;
      const ptUrl = `${origin}${bare === '/' ? '/pt' : `/pt${bare}`}`;

      // Remove any old hreflang we previously injected
      document.querySelectorAll('link[data-hreflang="ybp"]').forEach((n) => n.remove());

      const addAlt = (hreflang, href) => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'alternate');
        l.setAttribute('hreflang', hreflang);
        l.setAttribute('href', href);
        l.setAttribute('data-hreflang', 'ybp');
        document.head.appendChild(l);
      };
      addAlt('en', enUrl);
      addAlt('pt', ptUrl);
      addAlt('x-default', enUrl);
    } catch (_) {
      /* SSR / older browsers — silent */
    }

    // Language
    const docLang = window.location.pathname.startsWith('/pt') ? 'pt-PT' : 'en';
    document.documentElement.setAttribute('lang', docLang);

    // Structured Data (Schema.org) for Organization and LocalBusiness
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://yourbrokerportugal.com/#organization",
          "name": "Your Broker Portugal",
          "url": "https://yourbrokerportugal.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://customer-assets.emergentagent.com/job_f38ad5a1-d236-45ee-88b7-e3d9b27d3481/artifacts/ode9rwtr_DFF9F010-F752-4035-B3AD-E6BC06DED29B.png"
          },
          "description": "Consultores imobiliários especializados em propriedades de luxo em Portugal",
          "sameAs": [
            "https://www.linkedin.com/company/yourbrokerportugal",
            "https://www.instagram.com/yourbrokerportugal"
          ]
        },
        {
          "@type": "RealEstateAgent",
          "@id": "https://yourbrokerportugal.com/#realestate",
          "name": "Your Broker Portugal",
          "image": "https://customer-assets.emergentagent.com/job_f38ad5a1-d236-45ee-88b7-e3d9b27d3481/artifacts/ode9rwtr_DFF9F010-F752-4035-B3AD-E6BC06DED29B.png",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "PT",
            "addressLocality": "Lisboa",
            "addressRegion": "Lisboa"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "Lisboa"
            },
            {
              "@type": "City",
              "name": "Porto"
            },
            {
              "@type": "City",
              "name": "Cascais"
            },
            {
              "@type": "State",
              "name": "Algarve"
            },
            {
              "@type": "City",
              "name": "Comporta"
            }
          ],
          "priceRange": "€€€",
          "telephone": "+351 XXX XXX XXX",
          "email": "info@yourbrokerportugal.com"
        },
        {
          "@type": "WebSite",
          "@id": "https://yourbrokerportugal.com/#website",
          "url": "https://yourbrokerportugal.com",
          "name": "Your Broker Portugal",
          "description": description,
          "inLanguage": ["pt-PT", "en-US"],
          "publisher": {
            "@id": "https://yourbrokerportugal.com/#organization"
          }
        }
      ]
    };

    // Insert or update structured data
    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

  }, [title, description, keywords, ogImage, url, type]);

  return null;
};

export default SEO;
